<script>
   /*************************************************************/
   /* AppContainer for training                                 */
   /* ---------------------------                               */
   /* app container for training a PLS1-DA model                */
   /*************************************************************/

   import { Button, Container } from 'svelte-controls-basic';
   import { plsmodel_get, plsres_get } from '@mdatools-apps/wasm/pls.js';
   import { excludedVarNumbers, app_residuals } from '@mdatools-apps/shared/utils';
   import { downloadResults, downloadModel, predict, getIndices, getStatMatrices } from '../utils';

   import Relabeled from '@mdatools-apps/shared/widgets/Relabeled.svelte';
   import Outliers from '@mdatools-apps/shared/widgets/Outliers.svelte';
   import Exclvars from '@mdatools-apps/shared/widgets/Exclvars.svelte';
   import Contributions from '@mdatools-apps/shared/components/Contributions.svelte';
   import PrepWidget from '@mdatools-apps/shared/components/prep/PrepWidget.svelte';

   import ResultTable from '../shared/ResultTable.svelte';
   import ModelCalculator from '../shared/ModelCalculator.svelte';
   import PCVCalculator from '../shared/PCVCalculator.svelte';
   import PLSDASettings from '../shared/PLSDASettings.svelte';
   import { resetMyState } from '../shared/states.svelte.js';
   import DataLoader from '../shared/DataLoader.svelte';

   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // get part of the session for this tab
   const s = $derived(session['train']);

   // properties for showing contributions
   let selected = $state(-1);
   let contributionIsShown = $state(false);

   // exclude variables whose positions provided in array "pos"
   function excludeVars(pos) {

      // check that array is not empty
      if (pos.length < 1) return;

      // find variables ID corresponding to the positions
      // this is needed if some of the variables have been removed already
      // the numbers this returns are the ones the refit EXCLUDES, so a model whose 'varindices'
      // is off by one would drop a variable the user never marked - which is why the list is
      // checked against the calibration block rather than trusted. See 'excludedVarNumbers'
      const exclvars = excludedVarNumbers(pos, s, s.model.pls);

      if (exclvars.length > 0) {

         // make sure there are no duplicates or already removed indices
         const exclvars_old_set = new Set(s.exclvars);
         const exclvars_filtered = [...new Set(exclvars)].filter(item => !exclvars_old_set.has(item));

         // add new indices to global array and reset all outcomes and model
         s.exclvars = s.exclvars.concat(exclvars_filtered);
         s.model = {pls: null, da: null};
         s.pvres = {pls: null, da: null};
         s.calres = {pls: null, da: null};
         s.pvset = null;
         s.pvscalars = null;
         s.ind = null;
         s.ncomp_selected = 1;
      }
   }

   // reset all excluded variables, outcomes and model
   function resetExclvars() {
      s.exclvars = [];
      s.model = {pls: null, da: null};
      s.pvres = {pls: null, da: null};
      s.calres = {pls: null, da: null};
      s.pvset = null;
      s.pvscalars = null;
      s.ind = null;
      s.ncomp_selected = 1;
   }

   // clear everything a change of the outlier or relabel lists makes invalid: the model, both
   // results, the pv-set and the class indices. the preprocessing is left alone: it never reads
   // the response column ('prep_fit' and 'prep_apply' take 'nresp'), and 'calsetp' keeps the
   // original dummy, the relabelling being applied from the list at fit and predict time. so a
   // relabel keeps it whatever its kind, 'ModelCalculator' stays mounted with its settings and a
   // stale error box until the next fit, as in pls, and the excluded variables stay: their
   // numbers are positions in 'calsetp', which is then unchanged
   function invalidateModel() {
      s.model = {pls: null, da: null};
      s.pvres = {pls: null, da: null};
      s.calres = {pls: null, da: null};
      s.pvset = null;
      s.pvscalars = null;
      s.ind = null;
      s.ncomp_selected = 1;
   }

   // an outlier change also invalidates a preprocessing fitted here ('create' - the widget refits
   // with the outliers left out); a model read from a file applies the parameters stored in it
   // and does not depend on which objects are excluded, and 'skip' has none. 'calsetp' is cleared
   // together with 'prepmodel' and never on its own: the widget applies a model whenever the data
   // is missing, so clearing it alone for a file model would only redo the same preprocessing
   function invalidateOutliers() {
      invalidateModel();
      if (s.prepType === 'create') {
         s.calsetp = null;
         s.prepmodel = null;
      }
   }

   // mark object with index "pos" as outlier
   function markOutlier(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.calres.pls.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.calres.pls.objindices[pos];

      // add the object to outliers array and reset the model
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         invalidateOutliers();
      }
   }

   // reset list of outliers, outcomes and the model
   function resetOutliers() {
      s.outliers = [];
      invalidateOutliers();
   }

   // remove last added outlier from the list
   function undoOutliers() {
      s.outliers.pop();
      invalidateOutliers();
   }

   // mark object with index "pos" as relabeled
   function markRelabeled(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.calres.pls.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.calres.pls.objindices[pos];

      if (!s.relabeled) {
         s['relabeled'] = [];
      }

      // add the object to relabeled array and reset the model
      if (id >= 0 && !s.relabeled.includes(id)) {
         s.relabeled.push(id);
         invalidateModel();
      }
   }

   // reset list of relabeled objects, outcomes and the model
   function resetRelabeled() {
      s.relabeled = [];
      invalidateModel();
   }

   // remove last added relabeled object from the list
   function undoRelabeled() {
      s.relabeled.pop();
      invalidateModel();
   }


   // shows a modal dialog with contributions plot for object with index "pos"
   function showContributions(pos) {
      if (pos < 0) return;
      selected = pos;
      contributionIsShown = true;
   }

   // hides the contribution plot dialog
   function hideContributions() {
      contributionIsShown = false;
      selected = -1;
   }

   // callback to set model parameters from heatmap plot
   function setModelParams(p) {
      s.model.da.ncomp = p[0];
      if (s.model.da.rule === 'hard') {
         s.model.da.threshold = p[1];
      } else {
         s.model.da.alpha = p[1];
      }
   }

   // if training set is set null - reset model and all related parameters
   $effect(() => {
      if (s.calset === null) {
         resetMyState();
         s.pvres = {pls: null, da: null};
         s.calres = {pls: null, da: null};
         s.pvset = null;
         s.pvscalars = null;
         s.calsetp = null;
         s.prepmodel = null;
         s.prepobj = [];
         s.prepType = null
         s.outliers = [];
         s.relabeled = [];
         s.exclvars = [];
         s.ind = null;
         s.ncomp_selected = 1;
         s.model = {pls: null, da: null};
      }
   });

   // if model object is set to null - reset dependent objects
   $effect(() => {
      if (s.model.pls === null) {
         s.model.da = null;
         s.calres = {pls: null, da: null};
         s.pvres = {pls: null, da: null};
         s.pvset = null;
         s.pvscalars = null;
         s.ind = null;
         s.ncomp_selected = 1;
      }
   });

   // no preprocessing model - no preprocessed data, and nothing computed from it. an effect
   // clears what it invalidates itself, not only the next link: the rest used to be cleared by
   // the effects above, which then had to run again in the same flush, so each depends on no
   // other re-running
   $effect(() => {
      if (!s.prepmodel) {
         s.calsetp = null;
         s.exclvars = [];
         s.model = {pls: null, da: null};
         s.calres = {pls: null, da: null};
         s.pvres = {pls: null, da: null};
         s.pvset = null;
         s.pvscalars = null;
         s.ind = null;
         s.ncomp_selected = 1;
      }
   });

   // no pv-set - no pv results and no pv scalars
   $effect(() => {
      if (s.pvset === null) {
         s.pvres = {pls: null, da: null};
         s.pvscalars = null;
      }
   });

   // no calsetp - no model, and nothing computed from it
   $effect(() => {
      if (!s.calsetp) {
         s.model = {pls: null, da: null};
         s.calres = {pls: null, da: null};
         s.pvres = {pls: null, da: null};
         s.pvset = null;
         s.pvscalars = null;
         s.ind = null;
         s.ncomp_selected = 1;
      }
   });


   // compute indeces of objects belonging to each class
   $effect(() => {
      if (s.calres && s.calres.pls) {
         s.ind = getIndices(s.calres.pls.Yref.getcolref(1), s.calset.classNames);
      }
   });


   // the heatmap's figures of merit: the model's parameters applied to a grid, 9 matrices of
   // 603 rows per result set and LV. derived, not stored: they were written into the session by
   // effects, serialised with every backup (tens of KB per set at 10 LVs) and rebuilt on every
   // mount
   const statmatCal = $derived(s.calres.pls && s.model.da ?
      getStatMatrices(s.calres.pls, s.model.da.params, s.ind, s.model.da.classNames) : null);
   const statmatPv = $derived(s.pvres.pls && s.model.da ?
      getStatMatrices(s.pvres.pls, s.model.da.params, s.ind, s.model.da.classNames) : null);

   // recalculate DA results for PV-set when DA parameters have changed
   $effect(() => {
      if (s.pvres.pls && s.model.da) {
         s.pvres.da = predict(s.pvres.pls, s.model.da, s.ind);
      }
   });

   // recalculate DA results for calibration set when DA parameters have changed
   $effect(() => {
      if (s.calres.pls && s.model.da) {
         s.calres.da = predict(s.calres.pls, s.model.da, s.ind);
      }
   });

   // reactive effect for backing up the session. the session is read by 'backupSession' when the
   // write is due, not here, and the first run is skipped: it only ever coincides with a session
   // the app was given as a whole, which is already on disk, is written by 'MainApp' itself, or is
   // a fresh one that need not be stored. the values after the function are the dependencies (see
   // 'backupSession'). 's.model.pls', not 's.model': 'ModelCalculator' binds the inner field, and
   // reading a parent does not subscribe to its children - so creating the model reached nothing
   // here and the fit was only stored once a PV-set followed. 'calres.pls' needs no entry of its
   // own, being assigned in the same flush. 'model.da.ncomp' is the LV slider, a 'Range' which
   // writes once per step crossing while the pointer is down; it was left out while a dependency
   // here serialized the whole session on each step, which the delay in 'backupSession' is for
   let mounted = false;
   $effect(() => {
      const deps = [s.calset, s.calsetp, s.pvres.pls, s.pvres.da, s.model.pls, s.model.da?.ncomp];
      if (mounted) backupSession(() => session, ...deps);
      mounted = true;
   });

   const indSelected = $derived.by(() => {
      if (selected < 0 || !s.calres.pls || !s.calres.pls.Yref) return null;
      const y = s.calres.pls.Yref.v[selected];
      const ci = y > 0 ? 0 : 1;
      const cn = s.model.da.classNames[ci];
      return s.ind[cn].map(v => v + 1);
   });
</script>

<div class="app-container">

   <!-- modal dialog with contribution plot -->
   {#if contributionIsShown && selected >= 0}
   <Contributions
      model_getter={plsmodel_get}
      res_getter={plsres_get}
      model={s.model.pls}
      res={{'cal': s.calres.pls, 'pv': s.pvres.pls}}
      residuals={(id) => WASM().then((M) => id === 'cal' ?
         app_residuals(M, s.model.pls, s.calsetp, s.calres.pls) :
         app_residuals(M, s.model.pls, s.pvset, s.pvres.pls))}
      ncomp={s.model.da.ncomp}
      {selected}
      ind={indSelected}
      closeModal={hideContributions}
   />
   {/if}

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={s.calset} classMandatory={true} />
      </div>

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.calset} {resetOutliers} {undoOutliers} />
      </div>
      {/if}

      {#if s.relabeled && s.relabeled.length > 0 }
      <div class="outliers-pane">
         <Relabeled relabeled={s.relabeled} dataset={s.calset} {resetRelabeled} {undoRelabeled} />
      </div>
      {/if}

      {#if s.calset }
         <PrepWidget
            {WASM}
            rawdata={s.calset}
            outliers={s.outliers}
            bind:prepType={s.prepType}
            bind:prepobj={s.prepobj}
            bind:prepdata={s.calsetp}
            bind:prepmeta={s.prepmeta}
            bind:prepmodel={s.prepmodel}
         />
      {/if}

      {#if s.exclvars && s.exclvars.length > 0 }
      <div class="exclvars-pane">
         <Exclvars exclvars={s.exclvars} {resetExclvars} />
      </div>
      {/if}

      {#if s.calsetp }
      <div class="model-calculator">
         <ModelCalculator
            {WASM}
            dataset={s.calsetp}
            relabeled={s.relabeled}
            outliers={s.outliers}
            exclvars={s.exclvars}
            bind:model={s.model.pls}
            bind:resobj={s.calres.pls}
         />
      </div>
      {/if}

      {#if s.calsetp && s.model.pls }
      <div class="pcv-calculator">
         <PCVCalculator
            {WASM}
            calset={s.calsetp}
            outliers={s.outliers}
            model={s.model.pls}
            calres={s.calres.pls}
            relabeled={s.relabeled}
            bind:pvset={s.pvset}
            bind:pvres={s.pvres.pls}
            bind:pvscalars={s.pvscalars}
            bind:ncompSelected={s.ncomp_selected}
         />
      </div>
      {/if}


      {#if s.model.pls && s.calres.pls && s.pvres.pls && s.ncomp_selected}
      <div class="ncomp-selector">
         <PLSDASettings
            plsmodel={s.model.pls}
            plsres={s.calres.pls}
            ind={s.ind}
            classNames={s.calset.classNames}
            ncomp={s.ncomp_selected}
            bind:damodel={s.model.da}
            keepSettings={true}
         />
         <Container label='Download'>
            <div class="buttons">
            <Button onclick={() => downloadResults(s.calres, s.calset, "train", s.model.da.ncomp)} text="Res (cal)"/>
            <Button onclick={() => downloadResults(s.pvres, s.pvset, "PV", s.model.da.ncomp)} text="Res (pv)" />
            <Button onclick={() => downloadModel(s.model, s.prepmodel)} text="Model" />
            </div>
         </Container>
      </div>
      {/if}
      {#if s.calres.da && s.pvres.da && s.model.da}
      <div class="result-stats">
         <ResultTable res={{cal: s.calres.da, pv: s.pvres.da}} model={s.model.da} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.calres.da || !s.pvres.da}
         <div class="help-text">
         {#if !s.calset}
            <p>
               This tab lets you train a PLS1-DA classification model and optimize it using Procrustes cross-validation.
               After that you can save the model to a file, apply it to a test set (tab <strong>Test</strong>)
               or to a new set of samples whose response us unknown (tab <strong>Predict</strong>).
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load your training/calibration set from a CSV file. The file should contain data as numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows
               correspond to observations or objects, columns — to variables (both response and predictors).
            </p>
            <p>
               First row in the CSV file can contain variable labels or values (<strong>header</strong>). It is assumed
               that variable lables are text (or have text parts), e.g. <em>"Na"</em>, <em>"OD260"</em>, etc. If x-variables
               are signals, e.g. spectra, this part of header can content numeric values, e.g. wavelength or wavenumbers.
               If this is the case, select <em>values</em> option for the header. This will let you to specify
               name of the values (e.g. <em>Wavelength</em>) and the units (e.g. <em>nm</em>) if necessary.
            </p>
            <p>
               First column in the file can contain labels for the objects/observations (<strong>row labels</strong>) — if so, just set
               the corresponding options after selecting the file.
            </p>
            <p>
               The column with class labels should be located right after the column with object/row labels. If there are no
               object labels, the response variable is expected to be in the first column. <strong>The column with class
                  labels is mandatory in the training set and it must contain labels for two classes.</strong>
            </p>
            <p>
               Here is an example how data structure may look like (note, that row labels and header are optional):
            </p>
            <div>
               <table>
                  <thead>
                  <tr>
                     <td>Name</td>
                     <td>Class</td>
                     <td>x1</td>
                     <td>x2</td>
                     <td>x3</td>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                     <td>O1</td>
                     <td>Red</td>
                     <td>3.8</td>
                     <td>1.3</td>
                     <td>13.6</td>
                  </tr>
                  <tr>
                     <td>O2</td>
                     <td>Blue</td>
                     <td>11.1</td>
                     <td>12.3</td>
                     <td>3.3</td>
                  </tr>
                  <tr>
                     <td>O3</td>
                     <td>Red</td>
                     <td>1.2</td>
                     <td>2.3</td>
                     <td>4.2</td>
                  </tr>
                  <tr>
                     <td>O3</td>
                     <td>Blue</td>
                     <td>12.2</td>
                     <td>12.3</td>
                     <td>14.2</td>
                  </tr>
                  </tbody>
               </table>
            </div>
            <p></p>
         {:else}
            {#if s.calset && !s.prepmodel }
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to decide how to preprocess the data. You can either skip preprocessing (in the model settings you
               will be able to apply centering and scaling), create the chain of preprocessing methods in special constructor
               (use <em>Create</em> to load the constructor) or load already built preprocessing model from a file. Make your choice.
            </p>
            {/if}

            {#if s.prepmodel && !s.model.pls}
            <p><strong>Step 3</strong></p>
            <p>
               Now you can create a global PLS model for the training set. Use more components/latent variables (LVs) than
               you expect to be optimal. You will have possibility to select the optimal number of components later.
            </p>
            {/if}

            {#if s.model && s.model.pls && !s.pvset}
            <p><strong>Step 4</strong></p>
            <p>
               Now set necessary parameters for Procrustes cross-validation and click button to compute the PV-set,
               which will help you to optimize PLS-DA model parameters. The parameters are the same as for conventional
               cross-validation: type of splits and number of segments. It is recommended to use fewer number of
               segments (e.g. 3, 4 or 5).
            </p>
            <p>
               After this step PLS-DA model will be created automatically and you will be able to explore it and tune
               its parameters.
            </p>
            {/if}

         {/if}
         </div>
      {:else}

         <!-- keyed on the model and not only guarded by it: a session file whose date equals the
              open session's, which is the same file loaded a second time, leaves the tabs mounted
              while every field is swapped for a new object at once, and the plot container
              captures its controls when it mounts -->
         {#key s.pvres && s.model }
         {#if s.calres.pls && s.pvres.pls && s.model.pls && s.model.da && s.calset && s.pvset}
         <AppPlots
            {markOutlier} {showContributions} {excludeVars} {setModelParams}
            {markRelabeled}
            data={{'cal': s.calset, 'pv': s.pvset}}
            res={{'cal': s.calres, 'pv': s.pvres}}
            scalars={s.pvscalars}
            model={s.model}
            statMat={{cal: statmatCal, pv: statmatPv}}
         />
         {/if}
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .result-stats {
      background: #e8e8e8;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table thead tr td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table tbody td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(330px, 30%) 1fr;
      }
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

</style>